import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	PcQuoteDetails,
	PrivateCarClient,
	UpdatePrivateCarQuoteCommand,
} from '@acorn-portal/shared/src/services/subscriptionAPI';
import { setSubscriptionData } from '@acorn-portal/shared/src/store/subscription.slice';
import { updateChangeOfExcess } from '@acorn-portal/shared/src/store/subscriptionChanges.slice';
import { useState } from 'react';
import { resetDrivers, setDriverData } from '@acorn-portal/shared/src/store/subscription-amend-drivers.slice';
import { useNavigate } from 'react-router';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';

export const useVoluntaryExcessChange = () => {
	const [isUpdatingExcess, setIsUpdatingExcess] = useState(false);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { quoteData, policyToken, effectiveDate } = useAppSelector((state) => state.subscription);

	const handleVoluntaryExcessChange = async (value: number) => {
		const currentExcess = quoteData?.quoteDetails.riskData.coverDetails.voluntaryExcess;

		if (value === currentExcess) {
			return;
		}

		setIsUpdatingExcess(true);

		try {
			const updatedQuoteData = structuredClone(quoteData);
			if (updatedQuoteData?.quoteDetails?.riskData?.coverDetails) {
				updatedQuoteData.quoteDetails.riskData.coverDetails.voluntaryExcess = value;
			} else {
				throw new Error('No cover details found on quote data');
			}

			// TODO: investigate bringing this into async thunk
			const url = import.meta.env.VITE_SUBSCRIPTION_API;
			const subscriptionClient = new PrivateCarClient(url, API);
			const quoteResponse = quoteData?.quoteResponse;
			const quoteReference = quoteResponse?.quoteRef as string;
			const quoteToken = quoteData?.quoteToken as string;

			const updateCommand: UpdatePrivateCarQuoteCommand = {
				quoteReference,
				quoteToken,
				quoteDetails: updatedQuoteData?.quoteDetails as PcQuoteDetails,
			};

			const response = await subscriptionClient.updateQuote(updateCommand);

			if (response?.quoteResponse?.underwrittenOptions?.[0].status === 'Quoted') {
				dispatch(
					setSubscriptionData({
						policyToken: policyToken ?? '',
						effectiveDate: effectiveDate ?? '',
						sendWelcomePackStatus: undefined,
						autoRenew: undefined,
						quoteData: response,
					}),
				);

				const driverDetails = response?.quoteDetails?.riskData?.driverDetails;
				if (driverDetails) {
					dispatch(setDriverData(driverDetails));
				} else {
					dispatch(resetDrivers());
				}

				dispatch(updateChangeOfExcess(true));
			} else {
				navigate(ROUTE_PATHS.error);
			}
		} catch (error) {
			console.error(error);
			navigate(ROUTE_PATHS.error);
		} finally {
			setIsUpdatingExcess(false);
		}
	};

	return {
		handleVoluntaryExcessChange,
		isUpdatingExcess,
	};
};
